import React, { useEffect } from 'react'
import {
  AgreementsPageProps,
  agreementsTableWidths
} from './AgreementsPage.types'
import styles from './AgreementsPage.module.scss'
import cl from 'classnames'
import {
  EntityCount,
  Text1,
  // PageHeader, // Remove because it's import wrong instead of shared component
  PaginationButtons,
  TableBodyContainer,
  TableBodyLine,
  TableContainer,
  // TableHeader
} from '@touche-lts/panel-ui/dist'
import {
  PageHeader,
  TableCellAgreementBudget,
  TableCellAgreementPeriod,
  TableCellWithAdditionalCount,
  TableHeader
} from 'components/shared'
import { IconAgreements } from 'assets'
import { inject } from 'mobx-react'
import { AGREEMENTS_TABLE_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import {
  AgreementsTableSortProps,
  AgreementStatusesLabels,
  getAgreementStatusByLabel
} from 'store/AgreementsTableStore/AgreementsTableStore.types'
import { AgreementStatusTypes, getAgreementStatusLabel } from 'models'
import { capitalize } from '@material-ui/core'
import {
  getProductsCount,
  getProjectsCellProps,
  getStoresCellProps
} from './AgreementsPage.utils'
import { useGetScrollWidth, User } from '../../../utilities'
import tableBodyContainerStyle from '../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'

const AgreementsPageComponent = (props: AgreementsPageProps) => {
  const {
    history: { push },
    [AGREEMENTS_TABLE_STORE]: {
      searchProps,
      onSearchPropsChange,
      getSortDirection,
      handleSortDirectionChange,
      isColumnMarked,
      paginationInfo,
      onPageSelect,
      triggerSearch,
      searchResults,
      budgetRange
    }
  } = props

  useEffect(triggerSearch, [triggerSearch])

  const buttons = []
  if (User.isAdmin() || User.isManager()) {
    buttons.push({
      preset: 'primary',
      children: 'Add an agreement',
      onClick: () => push('/agreements/add')
    })
  }

  const scrollbarSpacing = 4.8

  const { scrollX, handleScroll } = useGetScrollWidth()

  return (
    <div className={cl(styles.pageContainer)} style={{ overflow: 'hidden' }}>
      <PageHeader
        navSegments={[
          {
            label: 'Agreements',
            icon: IconAgreements as any
          }
        ]}
        buttons={buttons}
      />
      <TableContainer style={{ marginTop: 27, overflowX: 'auto' }} className={styles.thinScroll} onScroll={handleScroll}>
        <TableHeader
          tableColumns={[
            {
              label: 'Agreements',
              // widthPercentage: agreementsTableWidths.agreements,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.name,
                onChange: name => onSearchPropsChange({ name })
              },
              sortDirection: getSortDirection(
                AgreementsTableSortProps.AGREEMENTS
              ),
              setSortDirection: handleSortDirectionChange(
                AgreementsTableSortProps.AGREEMENTS
              ),
              isColumnMarked: isColumnMarked(
                AgreementsTableSortProps.AGREEMENTS
              ),
              widthPx: agreementsTableWidths.agreements
            },
            {
              label: 'Stores',
              // widthPercentage: agreementsTableWidths.stores,
              preset: 'grouped',
              groupLabel: 'Scope',
              // groupWidthPercentage:
              //   agreementsTableWidths.stores +
              //   agreementsTableWidths.projects +
              //   agreementsTableWidths.products,
              groupWidth: agreementsTableWidths.stores +
              agreementsTableWidths.projects +
              agreementsTableWidths.products,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.brandName,
                onChange: brandName => onSearchPropsChange({ brandName })
              },
              sortDirection: getSortDirection(AgreementsTableSortProps.STORES),
              setSortDirection: handleSortDirectionChange(
                AgreementsTableSortProps.STORES
              ),
              isColumnMarked: isColumnMarked(AgreementsTableSortProps.STORES),
              widthPx: agreementsTableWidths.stores
            },
            {
              label: 'Projects',
              // widthPercentage: agreementsTableWidths.projects,
              preset: 'grouped',
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.projectName,
                onChange: projectName => onSearchPropsChange({ projectName })
              },
              widthPx: agreementsTableWidths.projects
            },
            {
              label: 'Products',
              // widthPercentage: agreementsTableWidths.products,
              preset: 'grouped',
              widthPx: agreementsTableWidths.products + scrollbarSpacing
            },
            {
              label: 'Budget remaining',
              // widthPercentage: agreementsTableWidths.budget,
              sortDirection: getSortDirection(AgreementsTableSortProps.BUDGET),
              setSortDirection: handleSortDirectionChange(
                AgreementsTableSortProps.BUDGET
              ),
              isColumnMarked: isColumnMarked(AgreementsTableSortProps.BUDGET),
              sliderFilterProps: {
                label: 'Remaining budget',
                min: budgetRange.min,
                max: budgetRange.max,
                onChange: ([minAgreementBudget, maxAgreementBudget]) =>
                  onSearchPropsChange({
                    minAgreementBudget,
                    maxAgreementBudget
                  })
              },
              widthPx: agreementsTableWidths.budget
            },
            {
              label: 'Period',
              // widthPercentage: agreementsTableWidths.period,
              sortDirection: getSortDirection(AgreementsTableSortProps.PERIOD),
              setSortDirection: handleSortDirectionChange(
                AgreementsTableSortProps.PERIOD
              ),
              isColumnMarked: isColumnMarked(AgreementsTableSortProps.PERIOD),
              periodFilterProps: {
                period: searchProps.agreementStart
                  ? {
                    start: searchProps.agreementStart,
                    end: searchProps.agreementEnd
                  }
                  : null,
                setPeriod: period =>
                  onSearchPropsChange({
                    agreementStart: period ? period.start : null,
                    agreementEnd: period ? period.end : null
                  })
              },
              widthPx: agreementsTableWidths.period
            },
            {
              label: 'Status',
              // widthPercentage: agreementsTableWidths.status,
              sortDirection: getSortDirection(AgreementsTableSortProps.STATUS),
              setSortDirection: handleSortDirectionChange(
                AgreementsTableSortProps.STATUS
              ),
              isColumnMarked: isColumnMarked(AgreementsTableSortProps.STATUS),
              selectProps: {
                options: Object.values(AgreementStatusesLabels).map(label => ({
                  label: capitalize(label.toLowerCase()),
                  value: getAgreementStatusByLabel(label)
                })),
                value: searchProps.agreementStatus,
                onChange: agreementStatus =>
                  onSearchPropsChange({ agreementStatus }),
                noneLabel: 'All'
              },
              widthPx: agreementsTableWidths.status
            }
          ]}
        />
        <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer} >
          {searchResults.map((agreement, i) => {
            return (
              <TableBodyLine
                key={`${agreement.id}${i}`}
                cells={[
                  {
                    // widthPercentage: agreementsTableWidths.agreements,
                    onClick: () => push(`/agreements/${agreement.id}`),
                    children: (
                      <Text1 bold overflowEllipsis title={agreement.name}>
                        {agreement.name}
                      </Text1>
                    ),
                    isColumnMarked: isColumnMarked(
                      AgreementsTableSortProps.AGREEMENTS
                    ),
                    widthPx: agreementsTableWidths.agreements
                  },
                  {
                    // widthPercentage: agreementsTableWidths.stores,
                    children: (
                      <TableCellWithAdditionalCount
                        {...getStoresCellProps(agreement)}
                        notAvailableLabel='Not selected'
                      />
                    ),
                    isColumnMarked: isColumnMarked(
                      AgreementsTableSortProps.STORES
                    ),
                    widthPx: agreementsTableWidths.stores
                  },
                  {
                    // widthPercentage: agreementsTableWidths.projects,
                    children: (
                      <TableCellWithAdditionalCount
                        {...getProjectsCellProps(agreement)}
                        notAvailableLabel='Not selected'
                      />
                    ),
                    widthPx: agreementsTableWidths.projects
                  },
                  {
                    // widthPercentage: agreementsTableWidths.products,
                    children: (
                      <EntityCount
                        count={getProductsCount(agreement)}
                        entity='product'
                        notAvailableLabel='Not selected'
                      />
                    ),
                    widthPx: agreementsTableWidths.products
                  },
                  {
                    // widthPercentage: agreementsTableWidths.budget,
                    children: (
                      <TableCellAgreementBudget
                        data={agreement}
                        notAvailableLabel='Not selected'
                      />
                    ),
                    isColumnMarked: isColumnMarked(
                      AgreementsTableSortProps.BUDGET
                    ),
                    widthPx: agreementsTableWidths.budget
                  },
                  {
                    // widthPercentage: agreementsTableWidths.period,
                    children: (
                      <TableCellAgreementPeriod
                        data={agreement}
                        notAvailableLabel='Not selected'
                      />
                    ),
                    isColumnMarked: isColumnMarked(
                      AgreementsTableSortProps.PERIOD
                    ),
                    widthPx: agreementsTableWidths.period
                  },
                  {
                    // widthPercentage: agreementsTableWidths.status,
                    children: (
                      <Text1
                        style={{
                          color:
                            agreement.agreementStatus ===
                            AgreementStatusTypes.AGREED
                              ? styles.primary
                              : styles.primaryContent
                        }}
                      >
                        {capitalize(
                          getAgreementStatusLabel(agreement.agreementStatus)
                        )}
                      </Text1>
                    ),
                    isColumnMarked: isColumnMarked(
                      AgreementsTableSortProps.STATUS
                    ),
                    widthPx: agreementsTableWidths.status
                  }
                ]}
              />
            )
          })}
        </TableBodyContainer>
        <PaginationButtons {...paginationInfo} onPageSelect={onPageSelect} style={ { width: scrollX } } />
      </TableContainer>
    </div>
  )
}

const AgreementsPage = inject(AGREEMENTS_TABLE_STORE)(
  observer(AgreementsPageComponent)
)
export { AgreementsPage }
